import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { authApi } from './services/api';
import { adminAuthApi } from './services/adminApi';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ApplicationPage from './pages/ApplicationPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import WrittenTestPage from './pages/WrittenTestPage';
import InterviewPage from './pages/InterviewPage';
import ResultPage from './pages/ResultPage';
import EducationPage from './pages/EducationPage';
import DocumentVerifyPage from './pages/document/DocumentVerifyPage';
import DocumentSelectPage from './pages/document/DocumentSelectPage';
import DocumentCompletePage from './pages/document/DocumentCompletePage';
import EducationManagement from './pages/admin/EducationManagement';
import SsoPage from './pages/admin/SsoPage';

function App() {
  const [applicant, setApplicant] = useState<any>(null);
  const [admin, setAdmin] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('hire_token');
    if (!token) return;
    try {
      const data = await authApi.getMe();
      setApplicant(data);
    } catch {
      localStorage.removeItem('hire_token');
    }
  }, []);

  const checkAdminAuth = useCallback(async () => {
    const token = localStorage.getItem('hire_admin_token');
    if (!token) return;
    try {
      const data = await adminAuthApi.getMe();
      setAdmin(data);
    } catch {
      localStorage.removeItem('hire_admin_token');
      localStorage.removeItem('hire_admin_email');
    }
  }, []);

  useEffect(() => {
    Promise.all([checkAuth(), checkAdminAuth()]).finally(() => setLoading(false));
  }, [checkAuth, checkAdminAuth]);

  const handleLogout = () => {
    localStorage.removeItem('hire_token');
    setApplicant(null);
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('hire_admin_token');
    localStorage.removeItem('hire_admin_email');
    setAdmin(null);
  };

  const handleAdminLogin = async () => {
    await checkAdminAuth();
  };

  // 인증 불필요 경로는 로딩 중에도 바로 렌더링
  const isPublicPath = window.location.pathname.startsWith('/education') || window.location.pathname.startsWith('/document');

  if (loading && !isPublicPath) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-indigo-600 mx-auto mb-3" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-sm text-slate-500">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <ToastProvider>
    <ConfirmProvider>
    <Router>
      <Routes>
        {/* 지원자 라우트 */}
        <Route path="/login" element={applicant ? <Navigate to="/" /> : <LoginPage />} />
        <Route path="/application" element={
          applicant ? <ApplicationPage applicant={applicant} onUpdate={checkAuth} /> : <Navigate to="/login" />
        } />
        <Route path="/written-test" element={
          applicant ? <WrittenTestPage applicant={applicant} /> : <Navigate to="/login" />
        } />
        <Route path="/interview" element={
          applicant ? <InterviewPage applicant={applicant} /> : <Navigate to="/login" />
        } />
        <Route path="/result" element={
          applicant ? <ResultPage applicant={applicant} onUpdate={checkAuth} /> : <Navigate to="/login" />
        } />
        <Route path="/" element={
          applicant ? <DashboardPage applicant={applicant} onLogout={handleLogout} onUpdate={checkAuth} /> : <Navigate to="/login" />
        } />

        {/* 교육 페이지 (토큰 기반, 공개) */}
        <Route path="/education/:token" element={<EducationPage />} />
        {/* 토큰 없이 진입해도 빈 화면 대신 안내를 띄운다 (구 메일 링크 대응) */}
        <Route path="/education" element={<EducationPage />} />

        {/* HR 문서 발급 라우트 (별도 인증 없음) */}
        <Route path="/document" element={<DocumentVerifyPage />} />
        <Route path="/document/select" element={<DocumentSelectPage />} />
        <Route path="/document/complete" element={<DocumentCompletePage />} />

        {/* 관리자 라우트 */}
        {/* admin 에서 바로 이동 (handbook #16) — 직원만 */}
        <Route path="/sso" element={<SsoPage onLogin={handleAdminLogin} />} />
        <Route path="/admin/login" element={
          admin ? <Navigate to="/admin" replace /> : <AdminLoginPage onLogin={handleAdminLogin} />
        } />
        {/* 미인증 시 URL을 바꾸지 않고 같은 자리에서 로그인 폼 렌더 → 딥링크(쿼리) 보존 */}
        <Route path="/admin/education" element={
          admin ? <EducationManagement admin={admin} onLogout={handleAdminLogout} /> : <AdminLoginPage onLogin={handleAdminLogin} />
        } />
        <Route path="/admin" element={
          admin ? <AdminDashboard admin={admin} onLogout={handleAdminLogout} /> : <AdminLoginPage onLogin={handleAdminLogin} />
        } />
      </Routes>
    </Router>
    </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;
